import React from "react";
import { cn } from "@heroui/react";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import { LockKeyhole } from "lucide-react";
import { parseCitationLink, renderTextWithLinks } from "@/shared/utils/helperFunctions";
import InlineCitationText from "./InlineCitationText";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";

const ProfileCard = ({
  title,
  icon: Icon,
  badge,
  variant = "gray", // "gray" | "green" | "red"
  type = "text", // "text" | "bullets" | "rich-bullets"
  data,
  description = "",
  cardData, // backward compatibility for legacy profile views
}) => {
  if (cardData) {
    const citation = parseCitationLink(cardData?.citation);
    return (
      <div className="flex h-full w-full flex-col rounded-[10px] border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 font-inter">
        {/* header */}
        <div className="bg-[#F9FAFB] dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-3 px-4 w-full flex shrink-0 items-center justify-start gap-2 rounded-t-lg text-gray-800 dark:text-gray-100 text-[0.763rem] leading-5 font-semibold">
          <LockKeyhole size={15} strokeWidth={2} className="text-gray-500 dark:text-gray-400 shrink-0" />
          {cardData?.category}
        </div>

        {/* body */}
        <div className="flex min-h-0 flex-1 flex-col gap-2 p-4 text-xs xl:text-[0.783rem] leading-5 text-gray-700 dark:text-gray-300 font-normal">
          <ul className="flex flex-col gap-2 list-none">
            {((cardData?.challenges || cardData?.priorities) || []).map((li, i) => (
              <li
                key={i}
                className="relative pl-4 before:content-['•'] before:absolute before:left-0 before:text-gray-500 dark:before:text-gray-400 before:font-bold font-normal text-gray-600 dark:text-gray-300"
              >
                <InlineCitationText>{li}</InlineCitationText>
              </li>
            ))}
          </ul>

          {/* footer */}
          {citation.href ? (
            <div className="mt-auto flex min-h-[40px] justify-end pt-2">
              <a
                href={citation.href}
                target="_blank"
                className="text-xs cursor-pointer bg-white dark:bg-gray-800 text-secondary font-inter p-2 rounded-lg flex justify-center items-center ml-auto font-medium border-gray-100 dark:border-gray-700 border shadow-sm"
                rel="noreferrer"
              >
                {citation.label}
              </a>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  const getHeaderStyle = () => {
    switch (variant) {
      case "green":
        return "bg-[#ECFDF5] dark:bg-emerald-950/40 border-[#D1FADF] dark:border-emerald-800 text-[#027A48] dark:text-emerald-300";
      case "red":
        return "bg-[#FEF3F2] dark:bg-red-950/40 border-[#FEE2E2] dark:border-red-800 text-[#B42318] dark:text-red-300";
      case "gray":
      default:
        return "bg-[#F9FAFB] dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100";
    }
  };

  const getBadgeVariant = () => {
    if (!badge) return "neutral";
    const b = String(badge).toLowerCase();
    if (["high", "growing", "grown", "shopping", "active", "success"].includes(b)) return "success";
    if (["medium", "med", "exploring", "stagnant", "warning", "warm"].includes(b)) return "warning";
    if (["low", "window shopping", "declining", "danger", "cold"].includes(b)) return "danger";
    return "neutral";
  };

  const getBadgeBorderClass = (v) => {
    switch (v) {
      case "success":
        return "border-success-200 dark:border-emerald-800 text-success-700 dark:text-emerald-300 bg-success-50 dark:bg-emerald-950/50";
      case "warning":
        return "border-warning-200 dark:border-amber-800 text-warning-700 dark:text-amber-300 bg-warning-50 dark:bg-amber-950/50";
      case "danger":
        return "border-error-200 dark:border-red-800 text-error-700 dark:text-red-300 bg-error-50 dark:bg-red-950/50";
      case "neutral":
      default:
        return "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800";
    }
  };

  return (
    <div className="flex h-full w-full flex-col rounded-[10px] border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden font-inter">
      {/* Header */}
      <div
        className={cn(
          "border-b py-3 px-4 w-full flex shrink-0 items-center justify-between gap-2 text-xs xl:text-[0.763rem] font-semibold leading-5",
          getHeaderStyle(),
        )}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} strokeWidth={2.2} className="shrink-0" />}
          <span>{title}</span>
        </div>
        {badge && (
          <StatusChip
            label={badge}
            variant={getBadgeVariant()}
            className={cn(
              "text-[10px] font-semibold rounded-full border px-2 py-0.5",
              getBadgeBorderClass(getBadgeVariant()),
            )}
          />
        )}
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-4 text-xs xl:text-[0.783rem] leading-5 text-gray-700 dark:text-gray-300 font-normal">
        {description && (
          <p className="whitespace-pre-line text-gray-600 dark:text-gray-300">
            {renderTextWithLinks(description)}
          </p>
        )}

        {type === "text" && (
          <p className="whitespace-pre-line text-gray-600 dark:text-gray-300">
            {renderTextWithLinks(
              typeof data === "string" || typeof data === "number" ? data : "",
            )}
          </p>
        )}

        {type === "bullets" && Array.isArray(data) && (
          <ul className="flex flex-col gap-2 list-none">
            {data.map((bullet, idx) => {
              if (typeof bullet !== "string") return null;
              const colonIndex = bullet.indexOf(":");
              if (colonIndex > 0 && colonIndex < 60) {
                const prefix = bullet.substring(0, colonIndex);
                const suffix = bullet.substring(colonIndex);
                return (
                  <li
                    key={idx}
                    className="relative pl-4 before:content-['•'] before:absolute before:left-0 before:text-gray-500 dark:before:text-gray-400 before:font-bold font-normal text-gray-600 dark:text-gray-300"
                  >
                    <span className="font-semibold text-gray-800 dark:text-gray-100">{prefix}</span>
                    {renderTextWithLinks(suffix)}
                  </li>
                );
              }
              return (
                <li
                  key={idx}
                  className="relative pl-4 before:content-['•'] before:absolute before:left-0 before:text-gray-500 dark:before:text-gray-400 before:font-bold font-normal text-gray-600 dark:text-gray-300"
                >
                  {renderTextWithLinks(bullet)}
                </li>
              );
            })}
          </ul>
        )}

        {type === "rich-bullets" && Array.isArray(data) && (
          <ul className="flex flex-col gap-2 list-none">
            {data.map((item, idx) => {
              if (typeof item === "string") {
                const colonIndex = item.indexOf(":");
                if (colonIndex > 0 && colonIndex < 60) {
                  const prefix = item.substring(0, colonIndex);
                  const suffix = item.substring(colonIndex);
                  return (
                    <li
                      key={idx}
                      className="relative pl-4 before:content-['•'] before:absolute before:left-0 before:text-gray-500 dark:before:text-gray-400 before:font-bold font-normal text-gray-600 dark:text-gray-300"
                    >
                      <span className="font-semibold text-gray-800 dark:text-gray-100">{prefix}</span>
                      {renderTextWithLinks(suffix)}
                    </li>
                  );
                }
                return (
                  <li
                    key={idx}
                    className="relative pl-4 before:content-['•'] before:absolute before:left-0 before:text-gray-500 dark:before:text-gray-400 before:font-bold font-normal text-gray-600 dark:text-gray-300"
                  >
                    {renderTextWithLinks(item)}
                  </li>
                );
              }
              return (
                <li
                  key={idx}
                  className="relative pl-4 before:content-['•'] before:absolute before:left-0 before:text-gray-500 dark:before:text-gray-400 before:font-bold font-normal text-gray-600 dark:text-gray-300"
                >
                  <span className="font-semibold text-gray-800 dark:text-gray-100">{item.title}</span>
                  {item.description && `: `}
                  {item.description && renderTextWithLinks(item.description)}
                  {item.citations && item.citations.length > 0 && (
                    <span className="inline-flex gap-1 ml-1 select-none">
                      {item.citations.map((url, cIdx) => (
                        <CustomCommonTooltip key={cIdx} content={url} placement="top">
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-secondary hover:underline text-[9px] font-medium align-super"
                          >
                            [{cIdx + 1}]
                          </a>
                        </CustomCommonTooltip>
                      ))}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ProfileCard;
